// src/components/layout/Layout.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getCurrentYear, getSchoolSettings } from '../../services/api';
import LoginAnnouncementModal from '../LoginAnnouncementModal';
import './Layout.css';

const ROLE_LABELS = {
  admin: 'ผู้ดูแลระบบ',
  academic: 'ครูวิชาการ',
  assessment: 'ครูวัดผล',
  teacher: 'ครูผู้สอน',
  homeroom: 'ครูประจำชั้น',
};

export default function Layout() {
  const { user, logout, isAdmin, isAcademic, isImpersonating, realAdmin, stopImpersonate } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentYear, setCurrentYear] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [schoolName, setSchoolName] = useState('');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(42);

  // วัดความสูง banner จริง (รองรับ wrap บนมือถือ)
  useEffect(() => {
    if (!bannerRef.current) return;
    const measure = () => {
      const h = bannerRef.current?.offsetHeight || 42;
      setBannerHeight(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, [isImpersonating]);

  // ตรวจจับขนาดหน้าจอ
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ปิด sidebar อัตโนมัติเมื่อเปลี่ยนหน้า (mobile)
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname]); // eslint-disable-line

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobileOpen(prev => !prev);
    } else {
      setCollapsed(c => {
        const next = !c;
        localStorage.setItem('sidebarCollapsed', next);
        return next;
      });
    }
  }, [isMobile]);

  useEffect(() => {
    getCurrentYear().then(res => {
      if (res.data.success) setCurrentYear(res.data.data);
    }).catch(() => {});
    getSchoolSettings().then(res => {
      const d = res.data.data;
      if (d?.school_name) setSchoolName(d.school_name);
    }).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonate = () => {
    stopImpersonate();
    navigate('/teachers');
  };

  const avatar = user ? (user.firstname?.charAt(0) || 'U') : 'U';

  return (
    <div className={`layout${isImpersonating ? ' layout-impersonate' : ''}`}
      style={isImpersonating ? { marginTop: bannerHeight, height: `calc(100vh - ${bannerHeight}px)` } : undefined}
    >
      {/* Banner แจ้งเตือนเมื่ออยู่ใน impersonate mode */}
      {isImpersonating && (
        <div ref={bannerRef} className="impersonate-banner">
          <div className="impersonate-banner-info">
            <span>🎭</span>
            <span className="impersonate-banner-name">
              {user?.prefix}{user?.firstname} {user?.lastname}
            </span>
            <span className="impersonate-banner-role">({ROLE_LABELS[user?.role]})</span>
          </div>
          <button onClick={handleStopImpersonate} className="impersonate-banner-btn">
            🔙 <span className="hide-mobile-text">กลับมาในนาม</span> {realAdmin?.firstname}
          </button>
        </div>
      )}

      <aside className={`sidebar${collapsed && !isMobile ? ' sidebar-collapsed' : ''}${isImpersonating ? ' sidebar-impersonate' : ''}${isMobile ? ' sidebar-mobile' : ''}${isMobile && mobileOpen ? ' sidebar-mobile-open' : ''}`}
        style={isMobile && isImpersonating ? { top: bannerHeight, height: `calc(100vh - ${bannerHeight}px)` } : undefined}
      >
        <div className="sidebar-logo">
          <div className="sidebar-logo-text">
            <h2>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:8,marginBottom:2}}>
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
              </svg>
              ระบบวัดผลการศึกษา
            </h2>
            <p style={{ fontSize: 12, opacity: 0.85, marginTop: 2, lineHeight: 1.4 }}>
              {schoolName || 'โรงเรียน'}
            </p>
          </div>
        </div>

        <nav className="sidebar-menu">
          <div className="sidebar-menu-group">หน้าหลัก</div>
          <NavLink to="/" end className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
            <span className="sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
            </span><span className="sidebar-item-label">แดชบอร์ด</span>
          </NavLink>

          <NavLink to="/attendance-daily-summary" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
            <span className="sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
            </span><span className="sidebar-item-label">สถิติรายวัน</span>
          </NavLink>

          {!isAcademic() && user?.role === 'teacher' && (
            <>
              <div className="sidebar-menu-group">การสอน</div>
              <NavLink to="/schedules" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/>
                  </svg>
                </span><span className="sidebar-item-label">ตารางสอน</span>
              </NavLink>
              <NavLink to="/subjects" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                </span><span className="sidebar-item-label">รายวิชา</span>
              </NavLink>
              <NavLink to="/score-classrooms" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                </span><span className="sidebar-item-label">กรอกคะแนน</span>
              </NavLink>
              <NavLink to="/clubs" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="5" r="3"/><path d="M12 8v4m-4 4h8m-8 0a4 4 0 0 0-4 4m12-4a4 4 0 0 1 4 4"/><circle cx="5" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
                  </svg>
                </span><span className="sidebar-item-label">ชุมนุม</span>
              </NavLink>
              <NavLink to="/attendance" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/>
                  </svg>
                </span><span className="sidebar-item-label">เช็คชื่อรายคาบ</span>
              </NavLink>
              <NavLink to="/my-students" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </span><span className="sidebar-item-label">จัดการนักเรียน</span>
              </NavLink>
              <NavLink to="/evaluation-report" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/>
                  </svg>
                </span><span className="sidebar-item-label">รายงานการประเมิน</span>
              </NavLink>
              <NavLink to="/teachers" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </span><span className="sidebar-item-label">ครู/บุคลากร</span>
              </NavLink>
            </>
          )}

          {isAcademic() && (
            <>
              <div className="sidebar-menu-group">จัดการระบบ</div>
              <NavLink to="/subjects" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                </span><span className="sidebar-item-label">รายวิชา</span>
              </NavLink>
              <NavLink to="/score-classrooms" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                </span><span className="sidebar-item-label">กรอกคะแนน</span>
              </NavLink>
              <NavLink to="/clubs" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="5" r="3"/><path d="M12 8v4m-4 4h8m-8 0a4 4 0 0 0-4 4m12-4a4 4 0 0 1 4 4"/><circle cx="5" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
                  </svg>
                </span><span className="sidebar-item-label">ชุมนุม</span>
              </NavLink>
              <NavLink to="/attendance" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/>
                  </svg>
                </span><span className="sidebar-item-label">เช็คชื่อรายคาบ</span>
              </NavLink>
              <NavLink to="/remedial" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                </span><span className="sidebar-item-label">กิจกรรมซ่อมเสริม</span>
              </NavLink>
              <NavLink to="/schedules" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="4" x2="9" y2="20"/><line x1="15" y1="4" x2="15" y2="20"/><line x1="3" y1="15" x2="21" y2="15"/>
                  </svg>
                </span><span className="sidebar-item-label">ตารางสอน</span>
              </NavLink>
              <NavLink to="/classrooms" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </span><span className="sidebar-item-label">จัดการนักเรียน</span>
              </NavLink>
              <NavLink to="/evaluation-report" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/>
                  </svg>
                </span><span className="sidebar-item-label">รายงานการประเมิน</span>
              </NavLink>
              {/* ตั้งค่าสถิติ / ครู/บุคลากร / ผู้ใช้งาน / ตั้งค่าระบบ: เฉพาะ admin เท่านั้น */}
              {isAdmin() && (
                <NavLink to="/classroom-settings" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                  <span className="sidebar-item-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="14" rx="1"/><rect x="14" y="8" width="7" height="9" rx="1"/><line x1="3" y1="21" x2="21" y2="21"/>
                    </svg>
                  </span><span className="sidebar-item-label">ตั้งค่าสถิติ</span>
                </NavLink>
              )}
              {/* ครู/บุคลากร: academic ดูได้เหมือนครูทั่วไป (isAdmin()=false จะซ่อน edit) */}
              <NavLink to="/teachers" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                <span className="sidebar-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </span><span className="sidebar-item-label">ครู/บุคลากร</span>
              </NavLink>
              {/* ผู้ใช้งาน / ตั้งค่าระบบ: เฉพาะ admin เท่านั้น */}
              {isAdmin() && !isImpersonating && (
                <NavLink to="/users" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                  <span className="sidebar-item-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0 1 12 0v2"/>
                    </svg>
                  </span><span className="sidebar-item-label">ผู้ใช้งาน</span>
                </NavLink>
              )}
              {isAdmin() && !isImpersonating && (
                <NavLink to="/school-settings" className={({isActive}) => `sidebar-item${isActive ? ' active' : ''}`}>
                  <span className="sidebar-item-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                  </span><span className="sidebar-item-label">ตั้งค่าระบบ</span>
                </NavLink>
              )}
            </>
          )}

        </nav>

        <div className="sidebar-footer">
          <div
            className="sidebar-user"
            style={user?.role === 'teacher' && !isImpersonating ? { cursor: 'pointer' } : {}}
            onClick={() => {
              if (user?.role === 'teacher' && !isImpersonating) navigate('/teachers?myprofile=1');
            }}
            title={user?.role === 'teacher' && !isImpersonating ? 'คลิกเพื่อแก้ไขข้อมูลส่วนตัว' : ''}
          >
            <div className="sidebar-avatar" style={isImpersonating ? {
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              boxShadow: '0 0 0 2px rgba(245,158,11,0.4)',
            } : {}}>
              {avatar}
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-username">{user?.prefix}{user?.firstname} {user?.lastname}</div>
              <div className="sidebar-role">
                {isImpersonating ? '· ' : ''}{ROLE_LABELS[user?.role]}
              </div>
            </div>
          </div>
          {isImpersonating ? (
            <button className="btn-logout" onClick={handleStopImpersonate}
              style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24', borderColor: 'rgba(245,158,11,0.3)' }}>
              🔙 <span className="sidebar-item-label">กลับมาในนาม Admin</span>
            </button>
          ) : (
            <button className="btn-logout" onClick={handleLogout}>
              🚪 <span className="sidebar-item-label">ออกจากระบบ</span>
            </button>
          )}
          <div className="sidebar-item-label" style={{ textAlign:'center', fontSize:'11px', color:'rgba(255,255,255,0.25)', marginTop:'10px', lineHeight:'1.5' }}>
            พัฒนาโดย นันทวิทย์ ชูเชื้อ
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      <div className="main-content">
        <div className="topbar">
          <button className="topbar-hamburger" onClick={toggleSidebar} title="ย่อ/ขยาย sidebar">
            <span /><span /><span />
          </button>
          <div className="topbar-title">ระบบวัดผลการศึกษา</div>
          {currentYear && (
            <div className="topbar-year">ปีการศึกษา {currentYear.year}</div>
          )}
        </div>
        <div className="page-content">
          <Outlet />
        </div>
      </div>

      <LoginAnnouncementModal />
    </div>
  );
}
